import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import idx from "idx";
import { errorSnackBar, successSnackBar } from "../../../actions/common.action";
import { loaders, SYNC_POLL_INTERVALS } from "../../../constants";
import { exportInvoicesBulk, exportBillsBulk, cancelSyncJob, getSyncJobStatus, getCustomerSyncStatus, syncPlatformContacts } from '../../../api/accounting.api';
import { getvendorsPOList } from '../../../actions/project-management.action';
import { getInvoiceAndProgressClaimList } from '../../../actions/invoice.action';
import { toDecimal, roundValue } from "../../../util/decimal-utils";

export function useInvoiceSync({
    selectedConnection,
    entityType,
    selectedProjectIds,
    billTab,
    projects,
    accountId,
    userId,
    userName,
    getSettingsOverride
}) {
    const dispatch = useDispatch();
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [syncing, setSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState({ total: 0, current: 0 });
    const [syncLogs, setSyncLogs] = useState({});
    const [checkingCustomerLink, setCheckingCustomerLink] = useState(false);
    const [customersSyncStatus, setCustomersSyncStatus] = useState({});
    const [forceSync, setForceSync] = useState(false);
    const [syncHappened, setSyncHappened] = useState(false);
    const latestFetchIdRef = useRef(0);
    const lastFetchedParamsRef = useRef({
        projectIds: null,
        billTab: null,
        entityType: null
    });
    const customerSyncCacheRef = useRef({});
    const invoicesRef = useRef([]);
    useEffect(() => {
        invoicesRef.current = invoices;
    }, [invoices]);

    const uniqueCustomers = useMemo(() => {
        const seen = new Set();
        const list = [];
        invoices.forEach(inv => {
            if (inv.customerId && !seen.has(inv.customerId)) {
                seen.add(inv.customerId);
                list.push({
                    id: inv.customerId,
                    name: inv.customerName
                });
            }
        });
        return list;
    }, [invoices]);

    const checkCustomerSyncStatus = useCallback(async () => {
        const connId = selectedConnection?.id;
        if (!connId || uniqueCustomers.length === 0) return;

        const cachedMap = customerSyncCacheRef.current[connId];
        const allCached = cachedMap && uniqueCustomers.every(cust => cachedMap[cust.id] !== undefined);

        if (allCached) {
            setCustomersSyncStatus(cachedMap);
            return;
        }

        setCheckingCustomerLink(true);
        try {
            const statusMap = { ...(cachedMap || {}) };
            await Promise.all(uniqueCustomers.map(async (cust) => {
                if (statusMap[cust.id] !== undefined) return;
                try {
                    const res = await getCustomerSyncStatus(connId, cust.id);
                    statusMap[cust.id] = {
                        isLinked: !!res?.isLinked,
                        customerName: cust.name,
                        syncing: false
                    };
                } catch (err) {
                    console.error(`Failed to check sync status for customer ${cust.id}:`, err);
                    statusMap[cust.id] = {
                        isLinked: false,
                        customerName: cust.name,
                        syncing: false
                    };
                }
            }));
            customerSyncCacheRef.current[connId] = statusMap;
            setCustomersSyncStatus(statusMap);
        } catch (err) {
            console.error("Failed to check customer sync statuses:", err);
        } finally {
            setCheckingCustomerLink(false);
        }
    }, [uniqueCustomers, selectedConnection]);

    const handleSyncCustomer = async (customerId) => {
        const connId = selectedConnection?.id;
        if (!customerId || !connId || !accountId) return;

        setCustomersSyncStatus(prev => ({
            ...prev,
            [customerId]: {
                ...prev[customerId],
                syncing: true
            }
        }));

        try {
            await dispatch(syncPlatformContacts(
                connId,
                'customer',
                [customerId],
                'push',
                undefined,
                { loader: loaders.NONE, successMessage: null }
            ));

            let linked = false;
            for (let i = 0; i < 6; i++) {
                await new Promise(resolve => setTimeout(resolve, 1500));
                const checkRes = await getCustomerSyncStatus(connId, customerId);
                if (checkRes && checkRes.success && checkRes.isLinked) {
                    linked = true;
                    break;
                }
            }

            if (linked) {
                dispatch(successSnackBar("Customer synced successfully"));
            } else {
                dispatch(errorSnackBar("Customer sync check timed out. Please check logs."));
            }
        } catch (err) {
            console.error("Failed to sync customer:", err);
            dispatch(errorSnackBar(err.message || "Failed to sync customer"));
        } finally {
            try {
                const res = await getCustomerSyncStatus(connId, customerId);
                const updatedStatus = {
                    isLinked: !!res?.isLinked,
                    customerName: uniqueCustomers.find(c => String(c.id) === String(customerId))?.name || '',
                    syncing: false
                };

                if (!customerSyncCacheRef.current[connId]) {
                    customerSyncCacheRef.current[connId] = {};
                }
                customerSyncCacheRef.current[connId][customerId] = updatedStatus;

                setCustomersSyncStatus(prev => ({
                    ...prev,
                    [customerId]: updatedStatus
                }));
            } catch (err) {
                if (customerSyncCacheRef.current[connId] && customerSyncCacheRef.current[connId][customerId]) {
                    customerSyncCacheRef.current[connId][customerId].syncing = false;
                }
                setCustomersSyncStatus(prev => ({
                    ...prev,
                    [customerId]: {
                        ...prev[customerId],
                        syncing: false
                    }
                }));
            }
        }
    };

    useEffect(() => {
        if (invoices.length > 0 && selectedConnection && entityType === 'invoice') {
            checkCustomerSyncStatus();
        } else {
            setCustomersSyncStatus(prev => Object.keys(prev).length === 0 ? prev : {});
        }
    }, [invoices, selectedConnection, checkCustomerSyncStatus, entityType]);

    const fetchInvoices = useCallback(async () => {
        const fetchId = ++latestFetchIdRef.current;
        setSyncLogs({});
        setSyncProgress({ total: 0, current: 0 });
        if (!selectedProjectIds || selectedProjectIds.length === 0) {
            setInvoices([]);
            lastFetchedParamsRef.current = {
                projectIds: null,
                billTab: null,
                entityType: null
            };
            return;
        }

        const currentParams = {
            projectIds: JSON.stringify(selectedProjectIds),
            billTab,
            entityType
        };

        const isSameParams =
            lastFetchedParamsRef.current.projectIds === currentParams.projectIds &&
            lastFetchedParamsRef.current.billTab === currentParams.billTab &&
            lastFetchedParamsRef.current.entityType === currentParams.entityType;

        if (isSameParams && invoices.length > 0) {
            return;
        }

        setLoadingInvoices(true);
        try {
            const isAllSelected = selectedProjectIds.includes("ALL") || selectedProjectIds.length === projects.length;

            if (entityType === 'invoice') {
                let formatted = [];
                if (isAllSelected) {
                    const res = await dispatch(getInvoiceAndProgressClaimList(`?projectId=ALL&type=list&minified=true`));
                    if (fetchId !== latestFetchIdRef.current) return;
                    const list = (res || []).filter(item => item.progressClaimId && item.invoice && item.invoice.id);
                    formatted = list.map(item => {
                        const claimAmount = item.claimAmount || 0;
                        const retentionAmount = item.retentionAmount || 0;
                        const retentionClaimAmount = item.retentionClaimAmount || 0;
                        const approved = roundValue(
                            toDecimal(claimAmount)
                                .minus(toDecimal(retentionAmount))
                                .plus(toDecimal(retentionClaimAmount)),
                            2
                        );
                        const approvedAmount = item.approvedAmount || 0;
                        const subTotal = roundValue(
                            toDecimal(approvedAmount).minus(toDecimal(retentionAmount)),
                            2
                        );
                        const amountInclGst = item.amountInclGst !== undefined && item.amountInclGst !== null
                            ? item.amountInclGst
                            : (item.totalAmount !== undefined && item.totalAmount !== null
                                ? item.totalAmount
                                : (item.total !== undefined && item.total !== null
                                    ? item.total
                                    : (item.grossAmount !== undefined && item.grossAmount !== null
                                        ? item.grossAmount
                                        : roundValue(toDecimal(approved).times(toDecimal(1.1)), 2))));

                        return {
                            id: item.invoice?.u_id || item.invoice?.uId || item.u_id || item.uId || String(item.invoice?.id),
                            uId: item.invoice?.u_id || item.invoice?.uId || item.u_id || item.uId || String(item.invoice?.id),
                            claimNumber: item.id,
                            invoiceNumber: item.invoice.id,
                            date: item.invoiceDate,
                            dueDate: item.dueDate,
                            claimAmount,
                            retentionAmount,
                            retentionClaimAmount,
                            approved,
                            amountInclGst,
                            subTotal,
                            status: item.progressClaimStatus || 'DRAFT',
                            customerName: idx(item, _ => _.project.customer.business_name) || 'Client',
                            customerId: idx(item, _ => _.project.customer.id),
                            projectName: projects.find(p => String(p.id) === String(idx(item, _ => _.project.id) || item.project_id))?.name || idx(item, _ => _.project.name) || 'Selected Project'
                        };
                    });
                } else {
                    const uniqueProjectIds = [...new Set(selectedProjectIds)];
                    const results = await Promise.all(uniqueProjectIds.map(projId =>
                        dispatch(getInvoiceAndProgressClaimList(`?projectId=${projId}&type=list&minified=true`))
                    ));
                    if (fetchId !== latestFetchIdRef.current) return;
                    const list = results.flat().filter(item => item && item.progressClaimId && item.invoice && item.invoice.id);
                    formatted = list.map(item => {
                        const claimAmount = item.claimAmount || 0;
                        const retentionAmount = item.retentionAmount || 0;
                        const retentionClaimAmount = item.retentionClaimAmount || 0;
                        const approved = roundValue(
                            toDecimal(claimAmount)
                                .minus(toDecimal(retentionAmount))
                                .plus(toDecimal(retentionClaimAmount)),
                            2
                        );
                        const approvedAmount = item.approvedAmount || 0;
                        const subTotal = roundValue(
                            toDecimal(approvedAmount).minus(toDecimal(retentionAmount)),
                            2
                        );
                        const amountInclGst = item.amountInclGst !== undefined && item.amountInclGst !== null
                            ? item.amountInclGst
                            : (item.totalAmount !== undefined && item.totalAmount !== null
                                ? item.totalAmount
                                : (item.total !== undefined && item.total !== null
                                    ? item.total
                                    : (item.grossAmount !== undefined && item.grossAmount !== null
                                        ? item.grossAmount
                                        : roundValue(toDecimal(approved).times(toDecimal(1.1)), 2))));

                        return {
                            id: item.invoice?.u_id || item.invoice?.uId || item.u_id || item.uId || String(item.invoice?.id),
                            uId: item.invoice?.u_id || item.invoice?.uId || item.u_id || item.uId || String(item.invoice?.id),
                            claimNumber: item.id,
                            invoiceNumber: item.invoice.id,
                            date: item.invoiceDate,
                            dueDate: item.dueDate,
                            claimAmount,
                            retentionAmount,
                            retentionClaimAmount,
                            approved,
                            amountInclGst,
                            subTotal,
                            status: item.progressClaimStatus || 'DRAFT',
                            customerName: idx(item, _ => _.project.customer.business_name) || 'Client',
                            customerId: idx(item, _ => _.project.customer.id),
                            projectName: projects.find(p => String(p.id) === String(idx(item, _ => _.project.id) || item.project_id))?.name || idx(item, _ => _.project.name) || 'Selected Project'
                        };
                    });
                }
                setInvoices(formatted);
                setSelectedIds([]);
            } else {
                const vendorType = billTab === 'contractor' ? 'sub-contractor' : 'Supplier';
                let formatted = [];
                if (isAllSelected) {
                    const res = await dispatch(getvendorsPOList({ vendorType, projectId: 'ALL', minified: true }));
                    if (fetchId !== latestFetchIdRef.current) return;
                    const list = (idx(res, _ => _.list) || []).filter(item => !item.isCreditInvoice);
                    formatted = list.map(item => {
                        const approvedVal = item.approvedAmount !== undefined && item.approvedAmount !== null ? item.approvedAmount : (item.approved !== undefined && item.approved !== null ? item.approved : undefined);
                        const retHeld = item.retainedAmount || 0;
                        const retRel = item.retentionReleasedAmount || 0;
                        const billAmount = billTab === 'contractor'
                            ? (approvedVal !== undefined
                                ? roundValue(toDecimal(approvedVal).minus(toDecimal(retHeld)).plus(toDecimal(retRel)), 2)
                                : (item.subTotal !== undefined && item.subTotal !== null ? item.subTotal : (item.amountPaid || 0)))
                            : (item.subTotal !== undefined && item.subTotal !== null ? item.subTotal : (item.amountPaid || 0));
                        const amountInclGst = billTab === 'contractor'
                            ? roundValue(toDecimal(billAmount).times(toDecimal(1.1)), 2)
                            : (item.amountInclGst !== undefined && item.amountInclGst !== null
                                ? item.amountInclGst
                                : (item.total !== undefined && item.total !== null
                                    ? item.total
                                    : (item.totalAmount !== undefined && item.totalAmount !== null
                                        ? item.totalAmount
                                        : (item.grossAmount !== undefined && item.grossAmount !== null
                                            ? item.grossAmount
                                            : roundValue(toDecimal(billAmount).times(toDecimal(1.1)), 2)))));
                        return {
                            id: item.invoiceId,
                            reference: item.invoiceNo,
                            poNumber: item.poNumber,
                            fId: item.fId || item.poNumber,
                            date: item.invoiceDate,
                            dueDate: item.dueDate,
                            amount: billAmount,
                            amountInclGst,
                            status: 'APPROVED',
                            vendorName: item.vendorName || '',
                            projectName: item.projectName,
                            vendorType,
                            retentionAmount: item.retainedAmount || 0,
                            retentionReleasedAmount: item.retentionReleasedAmount || 0
                        };
                    });
                } else {
                    const uniqueProjectIds = [...new Set(selectedProjectIds)];
                    const results = await Promise.all(uniqueProjectIds.map(projId =>
                        dispatch(getvendorsPOList({ vendorType, projectId: projId, minified: true }))
                    ));
                    if (fetchId !== latestFetchIdRef.current) return;
                    const list = results.map(res => idx(res, _ => _.list) || []).flat().filter(item => item && !item.isCreditInvoice);
                    formatted = list.map(item => {
                        const approvedVal = item.approvedAmount !== undefined && item.approvedAmount !== null ? item.approvedAmount : (item.approved !== undefined && item.approved !== null ? item.approved : undefined);
                        const retHeld = item.retainedAmount || 0;
                        const retRel = item.retentionReleasedAmount || 0;
                        const billAmount = billTab === 'contractor'
                            ? (approvedVal !== undefined
                                ? roundValue(toDecimal(approvedVal).minus(toDecimal(retHeld)).plus(toDecimal(retRel)), 2)
                                : (item.subTotal !== undefined && item.subTotal !== null ? item.subTotal : (item.amountPaid || 0)))
                            : (item.subTotal !== undefined && item.subTotal !== null ? item.subTotal : (item.amountPaid || 0));
                        const amountInclGst = billTab === 'contractor'
                            ? roundValue(toDecimal(billAmount).times(toDecimal(1.1)), 2)
                            : (item.amountInclGst !== undefined && item.amountInclGst !== null
                                ? item.amountInclGst
                                : (item.total !== undefined && item.total !== null
                                    ? item.total
                                    : (item.totalAmount !== undefined && item.totalAmount !== null
                                        ? item.totalAmount
                                        : (item.grossAmount !== undefined && item.grossAmount !== null
                                            ? item.grossAmount
                                            : roundValue(toDecimal(billAmount).times(toDecimal(1.1)), 2)))));
                        return {
                            id: item.invoiceId,
                            reference: item.invoiceNo,
                            poNumber: item.poNumber,
                            fId: item.fId || item.poNumber,
                            date: item.invoiceDate,
                            dueDate: item.dueDate,
                            amount: billAmount,
                            amountInclGst,
                            status: 'APPROVED',
                            vendorName: item.vendorName || '',
                            projectName: item.projectName,
                            vendorType,
                            retentionAmount: item.retainedAmount || 0,
                            retentionReleasedAmount: item.retentionReleasedAmount || 0
                        };
                    });
                }
                setInvoices(formatted);
                setSelectedIds([]);
            }
            lastFetchedParamsRef.current = currentParams;
        } catch (err) {
            console.error("Failed to fetch invoices/bills for sync:", err);
        } finally {
            if (fetchId === latestFetchIdRef.current) {
                setLoadingInvoices(false);
            }
        }
    }, [dispatch, entityType, selectedProjectIds, projects.length, billTab, invoices.length]);

    const handleSelectAll = () => {
        if (selectedIds.length === invoices.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(invoices.map(inv => inv.id));
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const currentJobIdRef = useRef(null);

    const pollJobStatus = async (connectionId, jobId, intervalMs = SYNC_POLL_INTERVALS.ACTIVE_SYNC, maxRetries = SYNC_POLL_INTERVALS.MAX_RETRIES) => {
        for (let retry = 0; retry < maxRetries; retry++) {
            if (!isMountedRef.current) return { success: false, error: 'Component unmounted' };
            if (currentJobIdRef.current !== jobId) return { success: false, error: 'Job cancelled' };

            try {
                const res = await getSyncJobStatus(connectionId, jobId);
                const job = res?.data || res;

                if (job.result) {
                    const resultsObj = job.result.result || job.result;
                    setSyncLogs(prev => {
                        const next = { ...prev };
                        Object.keys(resultsObj).forEach(resKey => {
                            const item = resultsObj[resKey];
                            if (item && typeof item === 'object' && item.status) {
                                const matchedInv = invoicesRef.current.find(inv =>
                                    String(inv.uId).toLowerCase() === String(resKey).toLowerCase() ||
                                    String(inv.id).toLowerCase() === String(resKey).toLowerCase()
                                );
                                const targetKey = matchedInv ? matchedInv.id : resKey;
                                next[targetKey] = {
                                    status: item.status === 'success' ? 'success' : 'error',
                                    msg: item.msg
                                };
                            }
                        });
                        return next;
                    });
                }

                if (job.progress) {
                    setSyncProgress(job.progress);
                }

                if (job.status === 'completed') {
                    return { success: true, result: job.result };
                }
                if (job.status === 'failed') {
                    return { success: false, error: job.error || 'Sync job failed' };
                }
                if (job.status === 'cancelled') {
                    return { success: false, error: 'Sync job cancelled' };
                }
            } catch (err) {
                console.error("Error polling job status:", err);
            }
            await new Promise(resolve => {
                const tId = setTimeout(resolve, intervalMs);
                if (!isMountedRef.current) clearTimeout(tId);
            });
        }
        return { success: false, error: 'Polling timeout exceeded' };
    };

    const handleExecuteSync = async () => {
        if (syncing) return;
        if (!selectedConnection) {
            dispatch(errorSnackBar("No active accounting connection selected"));
            return;
        }

        const currentIds = invoices.map(inv => inv.id);
        const idsToSync = selectedIds.length === 0 ? currentIds : selectedIds.filter(id => currentIds.includes(id));
        if (idsToSync.length === 0) {
            dispatch(errorSnackBar("No items available to push"));
            return;
        }

        setSyncing(true);
        setSyncHappened(true);
        setSyncProgress({ total: idsToSync.length, current: 0 });

        const logs = {};
        idsToSync.forEach(id => {
            logs[id] = { status: 'pending', msg: 'Waiting in queue...' };
        });
        setSyncLogs(logs);

        const settingsOverride = getSettingsOverride();

        try {
            let res;
            if (entityType === 'invoice') {
                res = await exportInvoicesBulk(selectedConnection.id, idsToSync, settingsOverride, forceSync);
            } else {
                res = await exportBillsBulk(selectedConnection.id, idsToSync, settingsOverride, forceSync);
            }

            const resData = res?.data || res;
            const jobId = resData?.jobId;
            const skippedCount = resData?.skipped || 0;
            const skippedIds = resData?.skippedIds || [];
            const isSuccess = !!(res && (res.success || resData?.success || jobId || skippedCount > 0));

            // Handle "all already synced" (no job created)
            if (isSuccess && !jobId && skippedCount > 0) {
                dispatch(successSnackBar(`All ${skippedCount} item(s) are already synced. Use "Force Re-sync" to push again.`));
                setSyncLogs(prev => {
                    const next = { ...prev };
                    skippedIds.forEach(skId => {
                        const matchedId = idsToSync.find(id => String(id) === String(skId));
                        if (matchedId) {
                            next[matchedId] = { status: 'success', msg: 'Already synced' };
                        }
                    });
                    return next;
                });
                setSyncing(false);
                currentJobIdRef.current = null;
                return;
            }

            // Mark skipped items immediately
            if (skippedIds.length > 0) {
                setSyncLogs(prev => {
                    const next = { ...prev };
                    skippedIds.forEach(skId => {
                        const matchedId = idsToSync.find(id => String(id) === String(skId));
                        if (matchedId) {
                            next[matchedId] = { status: 'success', msg: 'Already synced (skipped)' };
                        }
                    });
                    return next;
                });
            }

            if (isSuccess && jobId) {
                currentJobIdRef.current = jobId;

                setSyncLogs(prev => {
                    const next = { ...prev };
                    idsToSync.forEach(id => {
                        next[id] = { status: 'syncing', msg: 'Syncing in background...' };
                    });
                    return next;
                });

                const pollRes = await pollJobStatus(selectedConnection.id, jobId);
                if (!isMountedRef.current || currentJobIdRef.current !== jobId) return;

                if (pollRes.success) {
                    const rawResultObj = pollRes.result?.result || pollRes.result || {};
                    let successCount = 0;
                    let failCount = 0;

                    setSyncLogs(prev => {
                        const next = { ...prev };
                        idsToSync.forEach(id => {
                            const matchedInv = invoicesRef.current.find(inv => String(inv.id) === String(id));
                            const targetKey = matchedInv ? (matchedInv.uId || matchedInv.id) : id;
                            const resItem = rawResultObj[targetKey] || rawResultObj[id] || rawResultObj[matchedInv?.id];

                            if (resItem && typeof resItem === 'object' && resItem.status) {
                                next[id] = {
                                    status: resItem.status === 'success' ? 'success' : 'error',
                                    msg: resItem.msg || resItem.errorDetails?.message || (resItem.status === 'success' ? 'Synced Successfully' : 'Failed to sync')
                                };
                            } else if (!next[id] || next[id].status === 'pending' || next[id].status === 'syncing') {
                                next[id] = {
                                    status: 'error',
                                    msg: 'Sync completed without individual status response'
                                };
                            }

                            if (next[id]?.status === 'success') successCount++;
                            else failCount++;
                        });
                        return next;
                    });

                    if (successCount > 0) {
                        dispatch(successSnackBar(`Successfully synced ${successCount} item(s)`));
                    }
                    if (failCount > 0) {
                        dispatch(errorSnackBar(`Failed to sync ${failCount} item(s). Check error details.`));
                    }
                } else {
                    const isCancelled = pollRes.error === 'Sync job cancelled';
                    const errMsg = isCancelled ? 'Sync was cancelled' : (pollRes.error || "Sync job failed");

                    if (isCancelled) {
                        dispatch(successSnackBar("Sync cancelled. Items processed before cancellation were saved."));
                    } else {
                        dispatch(errorSnackBar(errMsg));
                    }

                    setSyncLogs(prev => {
                        const next = { ...prev };
                        idsToSync.forEach(id => {
                            if (next[id]?.status === 'pending' || next[id]?.status === 'syncing') {
                                next[id] = {
                                    status: isCancelled ? 'cancelled' : 'error',
                                    msg: isCancelled ? 'Cancelled before processing' : errMsg
                                };
                            }
                        });
                        return next;
                    });
                }
            } else {
                dispatch(errorSnackBar(res?.error || "Failed to start bulk export"));
            }
        } catch (err) {
            const errMsg = idx(err, _ => _.response.data.error) || err.message || 'Network/Server Error';
            dispatch(errorSnackBar(errMsg));
            setSyncLogs(prev => {
                const next = { ...prev };
                idsToSync.forEach(id => {
                    if (next[id]?.status === 'pending' || next[id]?.status === 'syncing') {
                        next[id] = { status: 'error', msg: errMsg };
                    }
                });
                return next;
            });
        } finally {
            if (isMountedRef.current) {
                setSyncing(false);
                currentJobIdRef.current = null;
            }
        }
    };

    const handleCancelSync = async () => {
        const jobId = currentJobIdRef.current;
        if (!jobId || !selectedConnection) return;

        // Don't clear jobId or stop syncing — let the poll loop detect 'cancelled' status
        dispatch(successSnackBar("Sync cancellation requested..."));

        try {
            await cancelSyncJob(selectedConnection.id, jobId);
        } catch (err) {
            console.error("Failed to cancel sync job on backend:", err);
        }
    };

    return {
        invoices,
        loadingInvoices,
        selectedIds,
        setSelectedIds,
        syncing,
        syncProgress,
        syncLogs,
        checkingCustomerLink,
        customersSyncStatus,
        syncHappened,
        setSyncHappened,
        forceSync,
        setForceSync,
        fetchInvoices,
        handleSelectAll,
        handleSelectRow,
        handleSyncCustomer,
        handleExecuteSync,
        handleCancelSync
    };
}
